// ATTICUS-ANALYTICS-COMMAND-CENTER-1 — Executive Summary lens.
//
// Level 1 (KPI cards w/ sparklines) + Level 2 (interactive charts) + Level 3
// (drilldown via the shared drawer). Everything reads /analytics/summary.
import React, { useEffect, useState, useMemo } from "react";
import { apiFetch, fmt } from "../../api";
import { toneForStatus } from "../../components/ui";
import { KpiCard, ChartCard, buildQS } from "../AnalyticsKit";
import { Donut, GroupedBar, Trend, HBar, TONE_HEX } from "../charts";

export default function ExecutiveLens({ filters, openDrill, onOpenClaim, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    apiFetch(`/analytics/summary?${buildQS(filters)}`)
      .then((d) => { if (live) { setData(d); setLoading(false); } })
      .catch((e) => { if (live) { setError(e.message || String(e)); setLoading(false); } });
    return () => { live = false; };
  }, [filters, reloadKey]);

  const k = data?.kpis || {};
  const c = data?.charts || {};
  const paidSpark = useMemo(() => (c.paid_trend || []).map((r) => r.paid), [c.paid_trend]);
  const openedSpark = useMemo(() => (c.opened_vs_closed || []).map((r) => r.opened), [c.opened_vs_closed]);

  const inventory = useMemo(() => (c.inventory_by_status || []).map((r) => ({
    name: r.status, value: r.count, color: TONE_HEX[toneForStatus(r.status)],
  })), [c.inventory_by_status]);

  const attention = useMemo(() => (c.attention_by_type || []).reduce((acc, r) => {
    const key = ATTN_LABEL[r.type] || r.type;
    const ex = acc.find((a) => a.name === key);
    if (ex) ex.value += r.count; else acc.push({ name: key, value: r.count, _type: r.type });
    return acc;
  }, []).sort((a, b) => b.value - a.value), [c.attention_by_type]);

  if (error) return <div className="an-error">Couldn't load analytics: {error}</div>;

  const m = (v) => fmt.money(v);
  const kpiLoading = loading && !data;

  return (
    <div className="an-stack">
      {/* Level 1 — KPI band */}
      <div className="an-kpis">
        <KpiCard label="Open Claims" tone="blue" value={kpiLoading ? "…" : num(k.open_claims)}
                 sub="active inventory" spark={openedSpark}
                 onClick={() => openDrill("open_inventory", null, "Open claims")} />
        <KpiCard label="New (period)" value={kpiLoading ? "…" : num(k.new_claims_period)}
                 sub={periodLabel(data)} onClick={() => openDrill("new_claims", null, "New claims this period")} />
        <KpiCard label="Closed (period)" value={kpiLoading ? "…" : num(k.closed_period)}
                 sub={periodLabel(data)} onClick={() => openDrill("closed_claims", null, "Closed this period")} />
        <KpiCard label="Total Reserve" tone="navy" value={kpiLoading ? "…" : m(k.total_reserve)}
                 sub="open claims" onClick={() => openDrill("open_inventory", null, "Open claims by reserve")} />
        <KpiCard label="Total Paid" value={kpiLoading ? "…" : m(k.total_paid)} sub="open claims" spark={paidSpark} />
        <KpiCard label="Remaining Reserve" tone="green" value={kpiLoading ? "…" : m(k.remaining_reserve)} sub="open claims" />
        <KpiCard label="Funds Requests Out" tone="amber"
                 value={kpiLoading ? "…" : num(k.funds_requests_outstanding?.count)}
                 sub={k.funds_requests_outstanding ? m(k.funds_requests_outstanding.amount) : "—"} />
        <KpiCard label="Open Bill Reviews"
                 value={kpiLoading ? "…" : (k.open_bill_reviews?.available ? num(k.open_bill_reviews.value) : "—")}
                 sub={k.open_bill_reviews?.available ? "in flight" : "no access"} />
        <KpiCard label="Open Tasks" value={kpiLoading ? "…" : num(k.open_tasks)}
                 tone={k.overdue_tasks > 0 ? "red" : undefined}
                 sub={k.overdue_tasks ? `${k.overdue_tasks} overdue` : "none overdue"} />
        <KpiCard label="AR Outstanding"
                 value={kpiLoading ? "…" : (k.ar_outstanding?.available ? m(k.ar_outstanding.amount) : "—")}
                 sub={k.ar_outstanding?.available ? `${k.ar_outstanding.count} sent` : "no access"} />
      </div>

      {/* Level 2 — charts */}
      <div className="an-grid">
        <ChartCard title="Claims Opened vs Closed" subtitle="rolling 12 months" wide
                   loading={kpiLoading} empty={!c.opened_vs_closed?.length}>
          <GroupedBar data={c.opened_vs_closed || []} xKey="month"
                      series={[{ key: "opened", label: "Opened", color: "#3b6fd4" },
                               { key: "closed", label: "Closed", color: "#10b981" }]}
                      onSegmentClick={(p) => openDrill(p._series === "closed" ? "closed_claims" : "new_claims",
                                                       null, p._series === "closed" ? "Closed this period" : "New claims this period")} />
        </ChartCard>

        <ChartCard title="Claim Inventory by Status" loading={kpiLoading} empty={!inventory.length}>
          <Donut data={inventory} onSegmentClick={(p) => openDrill("inventory_status", p.name, `${p.name} claims`)} />
        </ChartCard>

        <ChartCard title="Attention Items by Type" subtitle="open items" loading={kpiLoading} empty={!attention.length}>
          <HBar data={attention} onSegmentClick={(p) => openDrill("attention_type", p._type, `${p.name}`)} color="#f59e0b" />
        </ChartCard>

        <ChartCard title="Payments Trend" subtitle="paid $ per month (excl. duplicates)" wide
                   loading={kpiLoading} empty={!c.paid_trend?.length}>
          <Trend data={c.paid_trend || []} xKey="month" money
                 series={[{ key: "paid", label: "Paid", color: "#3b6fd4" }]} />
        </ChartCard>

        <ChartCard title="Top Financial Exposures" subtitle="largest open reserves" wide
                   loading={kpiLoading} empty={!c.top_exposures?.length}
                   footer={<button className="an-link" onClick={() => openDrill("open_inventory", null, "All open claims by reserve")}>View all open claims ↗</button>}>
          <table className="an-exposure-table">
            <thead>
              <tr><th>Claim #</th><th>Insured</th><th className="r">Total Reserve</th>
                  <th className="r">Paid</th><th className="r">Remaining</th><th></th></tr>
            </thead>
            <tbody>
              {(c.top_exposures || []).map((r) => (
                <tr key={r.claim_id}>
                  <td className="mono">{r.claim_number}</td>
                  <td>{r.insured || "—"}</td>
                  <td className="r num">{m(r.total_reserve)}</td>
                  <td className="r num">{m(r.total_paid)}</td>
                  <td className="r num">{m(r.remaining)}</td>
                  <td><button className="an-link" onClick={() => onOpenClaim && onOpenClaim(r.claim_id)}>Open ↗</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartCard>
      </div>
    </div>
  );
}

const ATTN_LABEL = {
  CLAIM_INACTIVE: "Inactive claim", PAYMENT_CODING_REQUIRED: "Payment coding",
  RESERVE_REVIEW: "Reserve review", PAYMENT_REVIEW: "Payment review",
  OVERDUE_TASK: "Overdue task", HIGH_PRIORITY_TASK: "High-priority task",
  DOCUMENT_REVIEW: "Document review",
};

function num(n) { return n == null ? "—" : Number(n).toLocaleString(); }
function periodLabel(data) {
  if (!data?.period) return "";
  return `${data.period.from} → ${data.period.to}`;
}
